package fyi.moko.hotpl.data.location

import fyi.moko.hotpl.domain.model.GeoPoint

/**
 * Platform-independent access to the device's current (last known) location and
 * the state of the location permission.
 *
 * Replaces the Android-only `HomeLocation` helpers (`FusedLocationProviderClient`
 * + `ContextCompat` permission check) so the "nearest" sort can run on both
 * Android and iOS. The *request* of the permission is handled separately by
 * [fyi.moko.hotpl.ui.home.rememberLocationPermissionRequest], because on
 * Android it is tied to the Compose `ActivityResultRegistry` lifecycle while on
 * iOS it is a direct `CLLocationManager` call — the two don't share a suspend
 * signature.
 *
 * - Android actual: `FusedLocationProviderClient.lastLocation` + Manifest permission.
 * - iOS actual: `CLLocationManager` (cached `location` or one-shot `requestLocation()`) + `authorizationStatus`.
 */
interface CurrentLocationProvider {

    /** Whether location access has already been granted (no prompt). */
    fun isPermissionGranted(): Boolean

    /**
     * The device's current location, or `null` if unavailable / not permitted.
     * Android reads the system-wide FusedLocation cache; iOS fast-paths on a
     * previously-delivered fix and otherwise issues a one-shot `requestLocation()`.
     */
    suspend fun lastKnownLocation(): GeoPoint?

    /**
     * The same location plus **how old the underlying fix is**, when the platform can say.
     *
     * 나이를 아는 것이 왜 필요한가: 캐시된 좌표는 몇 시간 전 것일 수 있고, 그걸 "지금 여기"의
     * 근거로 쓰면 가게 앞에 서 있는 사용자의 기록이 '안 가본 곳'이 된다
     * ([fyi.moko.hotpl.domain.model.inferVisit] 참고). 나이를 모르는 플랫폼은 null을 돌려주고,
     * 그러면 추론은 위치를 아예 쓰지 않는다 — 모를 때 추측하지 않는다는 원칙 그대로다.
     */
    suspend fun lastKnownFix(): LocationFix? = lastKnownLocation()?.let { LocationFix(it, null) }
}

/** A location and the age of the fix it came from ([ageMillis] null = unknown). */
data class LocationFix(val point: GeoPoint, val ageMillis: Long?)